<template>
    <DialogRoot v-slot="{ open }" class="overflow-hidden">
        <!-- Only rendered when a trigger is actually supplied. An empty DialogTrigger renders a
             comment node, and reka resolves that to its nextElementSibling — registering an
             arbitrary unrelated element as "the trigger" that focus returns to on close. -->
        <DialogTrigger v-if="$slots.trigger" :as-child="asChild">
            <slot name="trigger"></slot>
        </DialogTrigger>
        <DialogPortal :disabled="disablePortal">
            <DialogOverlay :class="[classes.overlay, overlayClass]" />
            <!-- Reka keeps closed content mounted during the exit animation. Inert prevents a
                 second click or key press from re-submitting a closing dialog (ENG-1249). -->
            <DialogContent
                :id="$slots.trigger ? undefined : fallbackContentId"
                :inert="!open"
                v-bind="description ? {} : { 'aria-describedby': undefined }"
                :class="[
                    classes.content,
                    layout === 'standard' && classes.standardLayout,
                    widthClasses[width ?? (layout === 'standard' ? 'default' : 'content')],
                    mobileHeight === 'expanded' ? classes.expandedHeight : classes.standardHeight,
                    reserveMobileNavSpace ? classes.mobileNavInset : classes.safeAreaInset,
                    dialogContentClass,
                ]"
                @open-auto-focus="handleOpenAutoFocus"
                @interact-outside="handleInteractOutside"
                @escape-key-down="handleInteractOutside"
                @close-auto-focus="handleCloseAutoFocus">
                <div v-if="layout === 'standard'" :class="classes.header">
                    <DialogTitle :class="[classes.title, 'nadia-subtitle-small']">{{ title }}</DialogTitle>
                    <DialogClose v-if="hasClose" as-child @click="emit('close')">
                        <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('common.closeDialog')" class="shrink-0">
                            <i class="bi bi-x-lg" aria-hidden="true"></i>
                        </BaseButton>
                    </DialogClose>
                </div>
                <VisuallyHidden v-else as-child>
                    <DialogTitle>{{ title }}</DialogTitle>
                </VisuallyHidden>
                <VisuallyHidden as-child>
                    <DialogDescription v-if="description">{{ description }}</DialogDescription>
                </VisuallyHidden>
                <div v-if="layout === 'custom' && hasClose" :class="[classes.customClose, floatClose && classes.floatingClose]">
                    <DialogClose as-child @click="emit('close')">
                        <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('common.closeDialog')">
                            <i class="bi bi-x-lg" aria-hidden="true"></i>
                        </BaseButton>
                    </DialogClose>
                </div>
                <template v-if="layout === 'standard'">
                    <div :class="[classes.body, $slots.footer && classes.withFooter]"><slot></slot></div>
                    <div v-if="$slots.footer" :class="classes.footer"><slot name="footer"></slot></div>
                </template>
                <slot v-else></slot>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { i18n } from "~vue/i18n";
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger, VisuallyHidden } from "reka-ui";
import type { ComponentPublicInstance } from "vue";
import { computed, useId } from "vue";

const { t } = i18n.global;

interface Props {
    asChild?: boolean;
    disablePortal?: boolean;
    disableCloseOnBackdropClick?: boolean;
    title: string;
    description?: string;
    /** Standard owns the title row, body spacing and actions. Custom is for full-bleed or independently composed flows. */
    layout?: "standard" | "custom";
    width?: "narrow" | "default" | "wide";
    /** Defaults to visible in the standard layout and hidden in custom layouts. */
    showClose?: boolean;
    // Custom layouts can float the close control over their own header/content.
    floatClose?: boolean;
    reserveMobileNavSpace?: boolean;
    mobileHeight?: "standard" | "expanded";
    /** Escape hatch for the portaled panel; prefer layout and width for shared anatomy. */
    dialogContentClass?: string;
    overlayClass?: string;
    onInteractOutside?: (event: Event) => void | null;
    // Where focus should land when the dialog closes. Only needed for v-model:open dialogs
    // whose opener no longer holds focus when the dialog mounts (e.g. a dropdown menu item
    // that unmounts as the menu closes); otherwise reka restores focus to the trigger, or to
    // whichever element was focused at open time.
    returnFocusTo?: HTMLElement | ComponentPublicInstance | null;
}

const props = withDefaults(defineProps<Props>(), {
    asChild: true,
    disablePortal: false,
    disableCloseOnBackdropClick: false,
    description: "",
    layout: "standard",
    showClose: undefined,
    floatClose: false,
    reserveMobileNavSpace: true,
    mobileHeight: "standard",
    overlayClass: "",
    returnFocusTo: null,
});

const emit = defineEmits<{ close: [] }>();
const hasClose = computed(() => props.showClose ?? props.layout === "standard");
const widthClasses = {
    narrow: "md:w-110 md:max-w-[calc(100dvw-3rem)]",
    default: "md:w-160 md:max-w-[calc(100dvw-3rem)]",
    wide: "md:w-240 md:max-w-[calc(100dvw-3rem)]",
    content: "",
};

// The close button is the first focusable element, so reka-ui's auto-focus would land on it and
// paint a focus ring in the corner every time the dialog opens. Focus the content instead.
const handleOpenAutoFocus = (event: Event) => {
    if (!hasClose.value) {
        return;
    }
    event.preventDefault();
    (event.target as HTMLElement | null)?.focus({ preventScroll: true });
};

// Without a DialogTrigger nothing seeds reka's content id, which would render as id="".
const fallbackContentId = useId();

const handleCloseAutoFocus = (event: Event) => {
    const target = props.returnFocusTo instanceof HTMLElement ? props.returnFocusTo : props.returnFocusTo?.$el;
    if (target instanceof HTMLElement && target.isConnected) {
        event.preventDefault();
        target.focus();
    }
};

const handleInteractOutside = (event: Event) => {
    if (props.disableCloseOnBackdropClick) {
        event.preventDefault();
        return;
    }
    // Prevent closing when the interaction targets a teleported element opted out of dismissal
    const originalTarget = (event as CustomEvent)?.detail?.originalEvent?.target as Element | null;
    if (originalTarget?.closest("[data-dismiss-ignore]")) {
        event.preventDefault();
        return;
    }
    if (props.onInteractOutside) {
        props.onInteractOutside(event);
    }
};
</script>

<style module="classes">
@reference "../../../css/tailwind.css";

@layer components {
    /* Colours come from css/tokens/dialog.css so the tonal flag can restyle
       the scrim and surface without a selector override here. */
    .overlay {
        /* Match content's layer so a later dialog's scrim covers an earlier dialog. */
        @apply bg-dialog-overlay/40 w-dvw h-dvh fixed top-0 left-0 z-30 data-[state=open]:animate-overlay-in data-[state=closed]:animate-dialog-out;
    }

    .content {
        @apply max-w-full md:max-w-max w-full m-0 bottom-0 md:bottom-auto fixed md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 focus-visible:outline-0 bg-dialog-surface rounded-b-none md:rounded-b-xl rounded-xl overflow-y-auto z-30 data-[state=open]:animate-sheet-in md:data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out;
    }

    .standardLayout {
        @apply flex flex-col border border-border shadow-lg text-ink overflow-hidden rounded-t-lgr md:rounded-lgr;
    }

    .header {
        @apply flex shrink-0 items-center gap-2 py-4 pl-6 pr-4 border-b border-border;
    }

    .title {
        @apply flex-1 min-w-0 break-words;
    }

    .body {
        @apply flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-6 pt-4;
    }

    /* The body scrolls as a whole. Shrinking an overflow-enabled child can collapse a file list
       to zero height and leave its controls covered by the following section. */
    .body > * {
        @apply shrink-0;
    }

    .footer {
        @apply flex shrink-0 flex-wrap items-center justify-end gap-2 p-4 border-t border-border;
    }

    .withFooter {
        @apply pb-4;
    }

    .customClose {
        @apply flex justify-end p-3;
    }

    .floatingClose {
        @apply absolute top-0 right-0 z-10;
    }

    .standardHeight {
        @apply max-h-[75dvh] md:max-h-[90dvh];
    }

    .expandedHeight {
        @apply max-h-[90dvh];
    }

    .mobileNavInset {
        @apply pb-(--mobile-nav-total-height) md:pb-0;
    }

    .safeAreaInset {
        @apply pb-[env(safe-area-inset-bottom)] md:pb-0;
    }
}
</style>
